// src/components/admin/RoleTemplateManager.tsx

import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit3, 
  Copy, 
  Trash2, 
  MoreVertical, 
  Users, 
  Package, 
  Clock, 
  AlertTriangle,
  CheckCircle,
  XCircle,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  Zap,
  Shield,
  Radio,
  Shirt,
  Car,
  Package2
} from 'lucide-react';
import { RoleTemplate } from '../../types/onboarding';
import { onboardingAPI } from '../../services/onboardingAPI';
import RoleTemplateModal from './RoleTemplateModal';

interface RoleTemplateManagerProps {
  // No props needed - self-contained component
}

const RoleTemplateManager: React.FC<RoleTemplateManagerProps> = () => {
  const [templates, setTemplates] = useState<RoleTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('active');
  const [selectedTemplate, setSelectedTemplate] = useState<RoleTemplate | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [expandedCard, setExpandedCard] = useState<string | null>(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await onboardingAPI.getRoleTemplates();
      
      if (response.success && response.data) {
        setTemplates(response.data);
      } else {
        throw new Error(response.error?.message || 'Failed to load role templates');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load role templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = () => {
    setSelectedTemplate(null);
    setIsCreateMode(true);
    setIsModalOpen(true);
  };

  const handleEditTemplate = (template: RoleTemplate) => {
    setSelectedTemplate(template);
    setIsCreateMode(false);
    setIsModalOpen(true);
  };

  const handleCloneTemplate = async (template: RoleTemplate) => {
    try {
      const clonedTemplate = {
        ...template,
        roleName: `${template.roleName} Copy`,
        displayName: `${template.displayName} (Copy)`,
        isActive: true
      };
      delete (clonedTemplate as any).templateId;
      delete (clonedTemplate as any).createdAt;
      delete (clonedTemplate as any).updatedAt;
      delete (clonedTemplate as any).createdBy;
      delete (clonedTemplate as any).updatedBy;

      const response = await onboardingAPI.createRoleTemplate(clonedTemplate);
      
      if (response.success) {
        await loadTemplates();
      } else {
        throw new Error(response.error?.message || 'Failed to clone template');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clone template');
    }
  };

  const handleToggleActive = async (template: RoleTemplate) => {
    try {
      const response = await onboardingAPI.updateRoleTemplate(template.templateId, {
        isActive: !template.isActive
      });
      
      if (response.success) {
        await loadTemplates();
      } else {
        throw new Error(response.error?.message || 'Failed to update template');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update template');
    }
  };

  const handleDeleteTemplate = async (template: RoleTemplate) => {
    if (!window.confirm(`Are you sure you want to delete the "${template.displayName}" template? This action cannot be undone.`)) {
      return;
    }

    try {
      // Since there's no delete endpoint, we'll deactivate instead
      const response = await onboardingAPI.updateRoleTemplate(template.templateId, {
        isActive: false
      });
      
      if (response.success) {
        await loadTemplates();
      } else {
        throw new Error(response.error?.message || 'Failed to delete template');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete template');
    }
  };

  const handleSaveTemplate = async (templateData: Partial<RoleTemplate>) => {
    try {
      let response;
      
      if (isCreateMode) {
        response = await onboardingAPI.createRoleTemplate(templateData);
      } else if (selectedTemplate) {
        response = await onboardingAPI.updateRoleTemplate(selectedTemplate.templateId, templateData);
      } else {
        throw new Error('No template selected for update');
      }
      
      if (response.success) {
        setIsModalOpen(false);
        setSelectedTemplate(null);
        await loadTemplates();
      } else {
        throw new Error(response.error?.message || 'Failed to save template');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save template');
    }
  };

  // Filter templates based on search and active state
  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.roleName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterActive === 'all' || 
                         (filterActive === 'active' && template.isActive) ||
                         (filterActive === 'inactive' && !template.isActive);
    
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'green' : 'gray';
  };

const getCategoryIcon = (categoryName: string) => {
  const iconMap: Record<string, React.ComponentType<any>> = {
    'Firearms': Zap,
    'Protective Equipment': Shield,  
    'Communication': Radio,
    'Duty Belt Equipment': Package,
    'Uniform Items': Shirt,
    'Vehicle Equipment': Car,
    'Supplies': Package2
  };
  
  const IconComponent = iconMap[categoryName] || Package2;
  return <IconComponent className="w-4 h-4 text-gray-600" />;
};

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-3 text-gray-600">Loading role templates...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Users className="w-7 h-7 mr-3 text-purple-600" />
            Role Templates
          </h1>
          <p className="text-gray-600 mt-1">
            Define equipment packages for different officer roles and positions
          </p>
        </div>
        
        <button
          onClick={handleCreateTemplate}
          className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Create Template</span>
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full rounded-md border-gray-300 focus:border-purple-500 focus:ring-purple-500"
              />
            </div>
          </div>
          
          {/* Filter */}
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value as 'all' | 'active' | 'inactive')}
              className="rounded-md border-gray-300 focus:border-purple-500 focus:ring-purple-500"
            >
              <option value="all">All Templates</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-400 mr-3" />
            <span className="text-red-700">{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-400 hover:text-red-600"
            >
              <XCircle className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Templates Grid */}
      {filteredTemplates.length === 0 ? (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm || filterActive !== 'all' ? 'No templates match your criteria' : 'No role templates found'}
          </h3>
          <p className="text-gray-500 mb-6">
            {searchTerm || filterActive !== 'all' 
              ? 'Try adjusting your search or filter settings'
              : 'Create your first role template to define equipment packages for officers'
            }
          </p>
          {!searchTerm && filterActive === 'all' && (
            <button
              onClick={handleCreateTemplate}
              className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700"
            >
              Create First Template
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredTemplates.map((template) => (
            <div key={template.templateId} className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              {/* Card Header */}
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {template.displayName}
                      </h3>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-${getStatusColor(template.isActive)}-100 text-${getStatusColor(template.isActive)}-800`}>
                        {template.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {template.description || 'No description provided'}
                    </p>
                  </div>
                  
                  {/* Actions Dropdown */}
                  <div className="relative">
                    <button
                      onClick={() => setExpandedCard(expandedCard === template.templateId ? null : template.templateId)}
                      className="p-1 text-gray-400 hover:text-gray-600 rounded"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                    
                    {expandedCard === template.templateId && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                        <div className="py-1">
                          <button
                            onClick={() => {
                              handleEditTemplate(template);
                              setExpandedCard(null);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                          >
                            <Edit3 className="w-4 h-4 mr-2" />
                            Edit Template
                          </button>
                          <button
                            onClick={() => {
                              handleCloneTemplate(template);
                              setExpandedCard(null);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                          >
                            <Copy className="w-4 h-4 mr-2" />
                            Clone Template
                          </button>
                          <button
                            onClick={() => {
                              handleToggleActive(template);
                              setExpandedCard(null);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                          >
                            {template.isActive ? (
                              <>
                                <XCircle className="w-4 h-4 mr-2" />
                                Deactivate
                              </>
                            ) : (
                              <>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Activate
                              </>
                            )}
                          </button>
                          <hr className="my-1" />
                          <button
                            onClick={() => {
                              handleDeleteTemplate(template);
                              setExpandedCard(null);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50 flex items-center"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Template
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Card Content */}
              <div className="p-4">
                {/* Statistics */}
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center">
                    <div className="text-lg font-semibold text-gray-900">
                      {template.categories?.length || 0}
                    </div>
                    <div className="text-xs text-gray-500">Categories</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-gray-900">
                      {template.estimatedDuration || 0}m
                    </div>
                    <div className="text-xs text-gray-500">Duration</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-gray-900">
                      {template.autoExpireHours || 72}h
                    </div>
                    <div className="text-xs text-gray-500">Auto-expire</div>
                  </div>
                </div>
                
                {/* Categories Preview */}
                {template.categories && template.categories.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Equipment Categories</h4>
                    <div className="space-y-2">
                      {template.categories.slice(0, 3).map((category, index) => (
                        <div key={index} className="flex items-center justify-between text-sm">
                          <div className="flex items-center space-x-2">
                            <span className="text-base">{getCategoryIcon(category.categoryName)}</span>
                            <span className="text-gray-700">{category.categoryName}</span>
                          </div>
                          <span className="text-gray-500">
                            {category.quantityRequired} {category.quantityRequired === 1 ? 'item' : 'items'}
                          </span>
                        </div>
                      ))}
                      {template.categories.length > 3 && (
                        <div className="text-xs text-gray-500 text-center">
                          +{template.categories.length - 3} more categories
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {(!template.categories || template.categories.length === 0) && (
                  <div className="text-center py-4 text-gray-500">
                    <Package className="w-6 h-6 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm">No categories configured</p>
                  </div>
                )}
              </div>
              
              {/* Card Footer */}
              <div className="px-4 py-3 bg-gray-50 rounded-b-lg">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    Modified: {new Date(template.updatedAt).toLocaleDateString()}
                  </span>
                  <button
                    onClick={() => handleEditTemplate(template)}
                    className="text-xs text-purple-600 hover:text-purple-700 font-medium"
                  >
                    Edit Template
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Role Template Modal */}
      {isModalOpen && (
        <RoleTemplateModal
          isOpen={isModalOpen}
          template={selectedTemplate}
          isCreateMode={isCreateMode}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedTemplate(null);
          }}
          onSave={handleSaveTemplate}
        />
      )}
    </div>
  );
};

export default RoleTemplateManager;